import { paywallInterceptor } from '@/components/organisms/PaywallEmailModal/src/utils/paywallInterceptor';
import type { Email, EmailTemplate } from '@/modules/email/emailTypes';
import type { EmailTemplateType } from '@/modules/sequencesRedesign/hooks/useEmailTemplates';
import service, { HttpResponseSuccess } from './service';

const apiService = service(`${process.env.REACT_BACKEND_HOST}`);

type SendEmailsPayload = {
	emails: Email[];
	isReply?: boolean;
};

export const sendEmails = ({
	emails,
	isReply = false
}: SendEmailsPayload): any => {
	paywallInterceptor({ apiInstance: apiService });

	const adminAccessed = Boolean(sessionStorage.admin_token);

	return apiService.post('/api/email/send', {
		emails,
		isReply,
		adminAccessed
	});
};

export const getEmailThread = (payload: any): any =>
	apiService.post('/api/email/getEmailThread', {
		payload
	});

export const getEmailContactedStatus = (payload: { toEmail: string }): any =>
	apiService.post('/api/email/getEmailContactedStatus', payload);

export const getEmailTemplates = (): Promise<
	HttpResponseSuccess<EmailTemplateType[]>
> => apiService.get('/api/template/get');

export const getEmailStatuses = (): Promise<HttpResponseSuccess<unknown>> =>
	apiService.get('/api/email/getEmailStatuses');

export const insertEmailTemplate = (
	payload: EmailTemplate
): Promise<HttpResponseSuccess<unknown>> =>
	apiService.post('/api/template/insert', payload);

export const updateEmailTemplate = (
	payload: EmailTemplate
): Promise<HttpResponseSuccess<unknown>> =>
	apiService.post('/api/template/update', payload);

export const deleteEmailTemplate = (
	templateId: string
): Promise<HttpResponseSuccess<unknown>> =>
	apiService.delete('/api/template/delete', { params: { templateId } });

export const duplicateEmailTemplate = (
	payload: EmailTemplate
): Promise<HttpResponseSuccess<unknown>> =>
	apiService.post('/api/template/duplicate', payload);

export const getScheduledEmailLimits = (): Promise<
	HttpResponseSuccess<unknown>
> => apiService.get('/api/email/getEmailLimits');

export const cancelScheduledEmail = (
	payload: any
): Promise<HttpResponseSuccess<unknown>> =>
	apiService.post('/api/email/cancelScheduledEmail', payload);

export const cancelScheduledBatchEmailsAdmin = (
	payload: any
): Promise<HttpResponseSuccess<unknown>> =>
	apiService.post('/api/email/cancelScheduledBatchEmailsAdmin', payload);

export const cancelScheduledBatchEmailsUser = (
	payload: any
): Promise<HttpResponseSuccess<unknown>> =>
	apiService.post('/api/email/cancelScheduledBatchEmailsUser', payload);

export const getScheduledEmailsCountByDate = (
	payload: any
): Promise<HttpResponseSuccess<unknown>> =>
	apiService.post('/api/email/getScheduledEmailsCountByDate', payload);

export const getScheduledEmails = (
	payload: any
): Promise<HttpResponseSuccess<unknown>> =>
	apiService.post('/api/email/getScheduledEmails', payload);

export const getScheduledEmailsCountByTimePeriod = (
	payload: any
): Promise<HttpResponseSuccess<unknown>> =>
	apiService.post('/api/email/getScheduledEmailsCountByTimePeriod', payload);

export const getEmailTemplateScoresRedesign = (
	payload: any
): Promise<HttpResponseSuccess<unknown>> =>
	apiService.post('/api/template/evaluate', payload);

export const sendAll = (
	payload: any
): Promise<HttpResponseSuccess<unknown>> => {
	paywallInterceptor({ apiInstance: apiService });

	return apiService.post('/api/email/sendAll', payload);
};

interface AISnippetResponse {
	snippets: {
		businessId: string;
		aiSnippet: string;
		creditsAvailable: number;
		generated: number;
		ignored: number;
		failed: number;
	}[];
}

export const getAISnippets = (
	payload: any
): Promise<HttpResponseSuccess<AISnippetResponse>> =>
	apiService.post('/api/template/generateAISnippets', payload);

export const regenAISnippet = (
	payload: any
): Promise<HttpResponseSuccess<unknown>> =>
	apiService.post('/api/template/regenerateAISnippet', payload);

export const getAICredits = (): Promise<HttpResponseSuccess<unknown>> =>
	apiService.get('/api/template/getAICredits');

export const sendCustomerReportBadData = (
	payload: any
): Promise<HttpResponseSuccess<unknown>> =>
	apiService.post('/api/outbound-email/sendCustomerReportBadData', payload);

export const getMonthlyEmailLimit = (): Promise<HttpResponseSuccess<unknown>> =>
	apiService.get('/api/email/getEmailMonthlyLimit');
